import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Users,
  DollarSign,
  AlertTriangle,
  Calendar,
  Building2,
  Mail,
  Phone,
  Loader2,
  MoreVertical,
  Eye,
  Pencil,
  Trash2,
  Clock,
  Tag,
  X,
  ExternalLink,
  Bell,
  Send,
  CheckCircle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { format, parseISO, differenceInDays } from 'date-fns';

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-green-100 text-green-800 border-green-300' },
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  expired: { label: 'Expired', color: 'bg-red-100 text-red-800 border-red-300' },
  paused: { label: 'Paused', color: 'bg-slate-100 text-slate-800 border-slate-300' }
};

const TYPE_CONFIG = {
  sponsor: { label: 'Sponsor', icon: '💎' },
  media: { label: 'Media Partner', icon: '📰' },
  agency: { label: 'Agency', icon: '🏢' },
  vendor: { label: 'Vendor', icon: '🛒' },
  influencer: { label: 'Influencer/Ambassador', icon: '⭐' },
  nonprofit: { label: 'Non-Profit/Charity', icon: '❤️' },
  technology: { label: 'Technology Partner', icon: '💻' },
  other: { label: 'Other', icon: '📋' }
};

export const PartnershipsPage = () => {
  const { api, user } = useAuth();
  const { subscribe } = useWebSocket();
  const [partnerships, setPartnerships] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPartnership, setEditingPartnership] = useState(null);
  const [viewingPartnership, setViewingPartnership] = useState(null);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({ partner_types: [], statuses: [], business_units: [] });
  const [newTag, setNewTag] = useState('');
  
  // Alert dialog state
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [sendingAlerts, setSendingAlerts] = useState(false);
  const [alertResult, setAlertResult] = useState(null);
  const [alertDaysThreshold, setAlertDaysThreshold] = useState('30');
  
  // Check if user can send alerts (admin or manager)
  const canSendAlerts = user?.role === 'admin' || user?.role === 'manager';

  const [formData, setFormData] = useState({
    name: '',
    partner_type: 'sponsor',
    status: 'active',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    description: '',
    business_unit: '',
    start_date: '',
    end_date: '',
    renewal_date: '',
    contract_value: '',
    deliverables: '',
    obligations: '',
    tags: [],
    notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [partnershipsRes, statsRes, configRes] = await Promise.all([
        api.get('/partnerships'),
        api.get('/partnerships/dashboard/stats'),
        api.get('/partnerships/config')
      ]);
      setPartnerships(partnershipsRes.data);
      setStats(statsRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const unsubs = [
      subscribe('partnership_created', fetchData),
      subscribe('partnership_updated', fetchData),
      subscribe('partnership_deleted', fetchData)
    ];
    return () => unsubs.forEach(unsub => unsub());
  }, [subscribe, fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        contract_value: formData.contract_value ? parseFloat(formData.contract_value) : null
      };
      
      if (editingPartnership) {
        await api.put(`/partnerships/${editingPartnership.id}`, payload);
      } else {
        await api.post('/partnerships', payload);
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save partnership:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (partnership) => {
    setEditingPartnership(partnership);
    setFormData({
      name: partnership.name,
      partner_type: partnership.partner_type,
      status: partnership.status,
      contact_name: partnership.contact_name || '',
      contact_email: partnership.contact_email || '',
      contact_phone: partnership.contact_phone || '',
      description: partnership.description || '',
      business_unit: partnership.business_unit || '',
      start_date: partnership.start_date || '',
      end_date: partnership.end_date || '',
      renewal_date: partnership.renewal_date || '',
      contract_value: partnership.contract_value ? String(partnership.contract_value) : '',
      deliverables: partnership.deliverables || '',
      obligations: partnership.obligations || '',
      tags: partnership.tags || [],
      notes: partnership.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this partnership?')) return;
    try {
      await api.delete(`/partnerships/${id}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete partnership:', error);
    }
  };

  const resetForm = () => {
    setEditingPartnership(null);
    setFormData({
      name: '',
      partner_type: 'sponsor',
      status: 'active',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      description: '',
      business_unit: '',
      start_date: '',
      end_date: '',
      renewal_date: '',
      contract_value: '',
      deliverables: '',
      obligations: '',
      tags: [],
      notes: ''
    });
  };

  // Send expiration alerts
  const handleSendAlerts = async () => {
    setSendingAlerts(true);
    setAlertResult(null);
    try {
      const response = await api.post(`/partnerships/send-expiration-alerts?days_threshold=${alertDaysThreshold}`);
      setAlertResult(response.data);
      if (response.data.emails_sent > 0) {
        toast.success(`Expiration alerts sent to ${response.data.emails_sent} team member(s)`);
      } else if (response.data.partnerships_count === 0) {
        toast.info('No partnerships expiring within the specified period');
      }
    } catch (error) {
      console.error('Failed to send alerts:', error);
      toast.error('Failed to send expiration alerts');
      setAlertResult({ error: error.response?.data?.detail || 'Failed to send alerts' });
    } finally {
      setSendingAlerts(false);
    }
  };

  // Filters
  const filteredPartnerships = partnerships.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.contact_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    const matchesType = typeFilter === 'all' || p.partner_type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value);
  };

  const getDaysUntilRenewal = (date) => {
    if (!date) return null;
    try {
      return differenceInDays(parseISO(date), new Date());
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6" data-testid="partnerships-page">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card className="mcc-card">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-blue-100 rounded-lg shrink-0">
                <Users className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xl md:text-2xl font-bold truncate">{stats?.summary?.total_partnerships || 0}</p>
                <p className="text-xs md:text-sm text-slate-500 truncate">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="mcc-card">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-green-100 rounded-lg shrink-0">
                <Building2 className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xl md:text-2xl font-bold truncate">{stats?.summary?.active || 0}</p>
                <p className="text-xs md:text-sm text-slate-500 truncate">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="mcc-card">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-purple-100 rounded-lg shrink-0">
                <DollarSign className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
              </div>
              <div className="min-w-0">
                <p className="text-lg md:text-2xl font-bold truncate">{formatCurrency(stats?.summary?.total_contract_value)}</p>
                <p className="text-xs md:text-sm text-slate-500 truncate">Value</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="mcc-card">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-amber-100 rounded-lg shrink-0">
                <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xl md:text-2xl font-bold truncate">{stats?.summary?.expiring_soon || 0}</p>
                <p className="text-xs md:text-sm text-slate-500 truncate">Expiring</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expiring Soon Alert */}
      {stats?.expiring_soon?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
              <div className="flex items-start gap-2 md:gap-3 flex-1 min-w-0">
                <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <h4 className="font-medium text-amber-800 text-sm md:text-base">Partnerships Expiring Soon</h4>
                  <div className="mt-1.5 md:mt-2 space-y-1">
                    {stats.expiring_soon.slice(0, 3).map(p => (
                      <p key={p.id} className="text-xs md:text-sm text-amber-700 truncate">
                        <strong>{p.name}</strong> - {p.days_remaining}d
                      </p>
                    ))}
                    {stats.expiring_soon.length > 3 && (
                      <p className="text-xs md:text-sm text-amber-600 font-medium">
                        + {stats.expiring_soon.length - 3} more
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {canSendAlerts && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100 w-full sm:w-auto"
                  onClick={() => setAlertDialogOpen(true)}
                  data-testid="send-alerts-btn"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  <span className="sm:inline">Send Alerts</span>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Send Alerts Action Card - Show when no expiring partnerships but user can send alerts */}
      {stats?.expiring_soon?.length === 0 && canSendAlerts && (
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-green-100 rounded-lg shrink-0">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-800 text-sm md:text-base">All Partnerships in Good Standing</h4>
                  <p className="text-xs md:text-sm text-slate-500">No partnerships expiring within 30 days</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="w-full sm:w-auto"
                onClick={() => setAlertDialogOpen(true)}
                data-testid="check-expiring-btn"
              >
                <Bell className="w-4 h-4 mr-2" />
                Check Expiring
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <Card className="mcc-card">
        <CardHeader className="border-b bg-slate-50/50 p-3 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4">
            <CardTitle className="text-base md:text-lg">Partnerships</CardTitle>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="gap-2 w-full sm:w-auto" data-testid="new-partnership-btn">
              <Plus className="w-4 h-4" />
              New Partnership
            </Button>
          </div>
          
          {/* Filters */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 md:gap-3 mt-3 md:mt-4">
            <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search partnerships..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-partnerships"
              />
            </div>
            
            <div className="flex gap-2 w-full sm:w-auto">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="flex-1 sm:w-[130px]" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="flex-1 sm:w-[140px]" data-testid="filter-type">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>{cfg.icon} {cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-0">
          <ScrollArea className="h-[400px] md:h-[500px]">
            <div className="divide-y">
              {filteredPartnerships.length === 0 ? (
                <div className="p-6 md:p-8 text-center text-slate-500 text-sm md:text-base">
                  No partnerships found. Create your first partnership to get started.
                </div>
              ) : (
                filteredPartnerships.map(partnership => {
                  const daysUntil = getDaysUntilRenewal(partnership.renewal_date);
                  const typeConfig = TYPE_CONFIG[partnership.partner_type] || TYPE_CONFIG.other;
                  const statusConfig = STATUS_CONFIG[partnership.status] || STATUS_CONFIG.active;
                  
                  return (
                    <div
                      key={partnership.id}
                      className="p-3 md:p-4 hover:bg-slate-50 transition-colors"
                      data-testid={`partnership-${partnership.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 md:gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 md:gap-2 mb-1 flex-wrap">
                            <span className="text-base md:text-lg">{typeConfig.icon}</span>
                            <h3 className="font-medium text-slate-900 truncate text-sm md:text-base">{partnership.name}</h3>
                            <Badge className={`${statusConfig.color} border text-xs`}>{statusConfig.label}</Badge>
                          </div>
                          
                          <p className="text-xs md:text-sm text-slate-500 line-clamp-1 mb-1.5 md:mb-2">{partnership.description || 'No description'}</p>
                          
                          <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs md:text-sm text-slate-600">
                            {partnership.contact_name && (
                              <span className="hidden sm:flex items-center gap-1">
                                <Users className="w-3 h-3 md:w-3.5 md:h-3.5" />
                                {partnership.contact_name}
                              </span>
                            )}
                            {partnership.contact_email && (
                              <a href={`mailto:${partnership.contact_email}`} className="hidden sm:flex items-center gap-1 text-blue-600 hover:underline truncate max-w-[200px]">
                                <Mail className="w-3 h-3 md:w-3.5 md:h-3.5 shrink-0" />
                                <span className="truncate">{partnership.contact_email}</span>
                              </a>
                            )}
                            {partnership.contract_value && (
                              <span className="flex items-center gap-1 font-medium text-green-700">
                                <DollarSign className="w-3 h-3 md:w-3.5 md:h-3.5" />
                                {formatCurrency(partnership.contract_value)}
                              </span>
                            )}
                            {partnership.renewal_date && (
                              <span className={`flex items-center gap-1 ${daysUntil !== null && daysUntil <= 30 ? 'text-amber-600 font-medium' : ''}`}>
                                <Calendar className="w-3 h-3 md:w-3.5 md:h-3.5" />
                                <span className="hidden sm:inline">Renews:</span> {partnership.renewal_date}
                                {daysUntil !== null && daysUntil <= 30 && ` (${daysUntil}d)`}
                              </span>
                            )}
                          </div>
                          
                          {/* Tags */}
                          {partnership.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5 md:mt-2">
                              {partnership.tags.slice(0, 3).map(tag => (
                                <Badge key={tag} variant="outline" className="text-[10px] md:text-xs">{tag}</Badge>
                              ))}
                              {partnership.tags.length > 3 && (
                                <Badge variant="outline" className="text-[10px] md:text-xs">+{partnership.tags.length - 3}</Badge>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="shrink-0">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setViewingPartnership(partnership)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEdit(partnership)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(partnership.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPartnership ? 'Edit Partnership' : 'New Partnership'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Partner Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Company or partner name"
                  required
                  data-testid="partnership-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Partner Type</Label>
                <Select value={formData.partner_type} onValueChange={(v) => setFormData({...formData, partner_type: v})}>
                  <SelectTrigger data-testid="partnership-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                      <SelectItem key={key} value={key}>{cfg.icon} {cfg.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger data-testid="partnership-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                      <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Business Unit</Label>
                <Select value={formData.business_unit || "none"} onValueChange={(v) => setFormData({...formData, business_unit: v === "none" ? "" : v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select business unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {config.business_units?.map(unit => (
                      <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Contract Value</Label>
                <Input
                  type="number"
                  value={formData.contract_value}
                  onChange={(e) => setFormData({...formData, contract_value: e.target.value})}
                  placeholder="50000"
                  data-testid="partnership-value"
                />
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Contact Name</Label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                  placeholder="John Smith"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  placeholder="john@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Phone</Label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  placeholder="+1 555-0100"
                />
              </div>
            </div>
            
            {/* Dates */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Renewal Date</Label>
                <Input
                  type="date"
                  value={formData.renewal_date}
                  onChange={(e) => setFormData({...formData, renewal_date: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the partnership"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Deliverables (What they provide)</Label>
                <Textarea
                  value={formData.deliverables}
                  onChange={(e) => setFormData({...formData, deliverables: e.target.value})}
                  placeholder="List of deliverables..."
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Obligations (What we owe)</Label>
                <Textarea
                  value={formData.obligations}
                  onChange={(e) => setFormData({...formData, obligations: e.target.value})}
                  placeholder="Our obligations..."
                  rows={3}
                />
              </div>
            </div>
            
            {/* Tags */}
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="gap-1">
                    {tag}
                    <X 
                      className="w-3 h-3 cursor-pointer" 
                      onClick={() => setFormData({...formData, tags: formData.tags.filter(t => t !== tag)})}
                    />
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add a tag..."
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newTag.trim()) {
                      e.preventDefault();
                      if (!formData.tags.includes(newTag.trim())) {
                        setFormData({...formData, tags: [...formData.tags, newTag.trim()]});
                      }
                      setNewTag('');
                    }
                  }}
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
                      setFormData({...formData, tags: [...formData.tags, newTag.trim()]});
                      setNewTag('');
                    }
                  }}
                >
                  <Tag className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Internal notes..."
                rows={2}
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving || !formData.name}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingPartnership ? 'Update' : 'Create'} Partnership
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={!!viewingPartnership} onOpenChange={(open) => !open && setViewingPartnership(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">{TYPE_CONFIG[viewingPartnership?.partner_type]?.icon}</span>
              {viewingPartnership?.name}
            </DialogTitle>
          </DialogHeader>
          
          {viewingPartnership && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={`${STATUS_CONFIG[viewingPartnership.status]?.color} border`}>
                  {STATUS_CONFIG[viewingPartnership.status]?.label}
                </Badge>
                <Badge variant="outline">{TYPE_CONFIG[viewingPartnership.partner_type]?.label}</Badge>
              </div>
              
              {viewingPartnership.description && (
                <p className="text-slate-600">{viewingPartnership.description}</p>
              )}
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <h4 className="font-medium text-sm text-slate-500 mb-2">Contact Information</h4>
                  {viewingPartnership.contact_name && <p className="text-sm">{viewingPartnership.contact_name}</p>}
                  {viewingPartnership.contact_email && (
                    <a href={`mailto:${viewingPartnership.contact_email}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {viewingPartnership.contact_email}
                    </a>
                  )}
                  {viewingPartnership.contact_phone && (
                    <p className="text-sm flex items-center gap-1"><Phone className="w-3 h-3" /> {viewingPartnership.contact_phone}</p>
                  )}
                </div>
                
                <div>
                  <h4 className="font-medium text-sm text-slate-500 mb-2">Key Dates</h4>
                  {viewingPartnership.start_date && <p className="text-sm">Start: {viewingPartnership.start_date}</p>}
                  {viewingPartnership.end_date && <p className="text-sm">End: {viewingPartnership.end_date}</p>}
                  {viewingPartnership.renewal_date && (
                    <p className="text-sm font-medium text-amber-600">
                      Renewal: {viewingPartnership.renewal_date}
                      {viewingPartnership.days_until_renewal !== null && ` (${viewingPartnership.days_until_renewal} days)`}
                    </p>
                  )}
                </div>
              </div>
              
              {viewingPartnership.contract_value && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium text-sm text-slate-500 mb-2">Contract Value</h4>
                  <p className="text-2xl font-bold text-green-700">{formatCurrency(viewingPartnership.contract_value)}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                {viewingPartnership.deliverables && (
                  <div>
                    <h4 className="font-medium text-sm text-slate-500 mb-2">Deliverables</h4>
                    <p className="text-sm whitespace-pre-wrap">{viewingPartnership.deliverables}</p>
                  </div>
                )}
                {viewingPartnership.obligations && (
                  <div>
                    <h4 className="font-medium text-sm text-slate-500 mb-2">Our Obligations</h4>
                    <p className="text-sm whitespace-pre-wrap">{viewingPartnership.obligations}</p>
                  </div>
                )}
              </div>
              
              {viewingPartnership.tags?.length > 0 && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium text-sm text-slate-500 mb-2">Tags</h4>
                  <div className="flex flex-wrap gap-1">
                    {viewingPartnership.tags.map(tag => (
                      <Badge key={tag} variant="outline">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {viewingPartnership.notes && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium text-sm text-slate-500 mb-2">Notes</h4>
                  <p className="text-sm whitespace-pre-wrap bg-slate-50 p-3 rounded">{viewingPartnership.notes}</p>
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setViewingPartnership(null)}>Close</Button>
                <Button onClick={() => { handleEdit(viewingPartnership); setViewingPartnership(null); }}>
                  <Pencil className="w-4 h-4 mr-2" /> Edit
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Expiration Alerts Dialog */}
      <Dialog open={alertDialogOpen} onOpenChange={(open) => { 
        if (!open) {
          setAlertResult(null);
          setAlertDaysThreshold('30');
        }
        setAlertDialogOpen(open); 
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              Send Partnership Expiration Alerts
            </DialogTitle>
            <DialogDescription>
              Send email notifications to admins and managers about partnerships expiring soon.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!alertResult ? (
              <>
                <div className="space-y-2">
                  <Label>Alert Threshold (Days)</Label>
                  <Select value={alertDaysThreshold} onValueChange={setAlertDaysThreshold}>
                    <SelectTrigger data-testid="alert-days-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-slate-500">
                    Partnerships expiring within {alertDaysThreshold} days will be included in the alert.
                  </p>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Email alerts will be sent to all users with Admin or Manager roles.
                  </p>
                </div>
              </>
            ) : alertResult.error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-800">
                  <X className="w-5 h-5" />
                  <strong>Error</strong>
                </div>
                <p className="text-sm text-red-700 mt-1">{alertResult.error}</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-800">
                    <CheckCircle className="w-5 h-5" />
                    <strong>Alerts Sent Successfully</strong>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Partnerships Included</p>
                      <p className="text-2xl font-bold text-green-700">{alertResult.partnerships_count}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Emails Sent</p>
                      <p className="text-2xl font-bold text-blue-700">{alertResult.emails_sent}</p>
                    </div>
                  </div>
                </div>
                
                {alertResult.partnerships?.length > 0 && (
                  <div className="space-y-2">
                    <Label>Partnerships Included in Alert</Label>
                    <ScrollArea className="h-[150px] border rounded-lg">
                      <div className="p-2 space-y-2">
                        {alertResult.partnerships.map(p => (
                          <div key={p.id} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded">
                            <span className="font-medium">{p.name}</span>
                            <Badge variant={p.days_remaining <= 7 ? 'destructive' : p.days_remaining <= 14 ? 'warning' : 'secondary'}>
                              {p.days_remaining} days
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setAlertDialogOpen(false)}>
              {alertResult ? 'Close' : 'Cancel'}
            </Button>
            {!alertResult && (
              <Button 
                onClick={handleSendAlerts} 
                disabled={sendingAlerts}
                data-testid="confirm-send-alerts-btn"
              >
                {sendingAlerts ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Alerts
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnershipsPage;
